import React, { useState, useCallback, useEffect } from 'react';
import { useMonitorData } from './hooks/useMonitorData';
import { useModelStats } from './hooks/useModelStats';
import { useFloorMusic } from './hooks/useFloorMusic';
import { TradeDialogData } from './types/tradingFloor.types';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from './constants/officeLayout';
import PixiOffice from './components/PixiOffice';
import TradeDialog from './components/TradeDialog';
import './TradingFloor.css';

/**
 * Standalone embed version of the Trading Floor — no navbar, no auth, no layout.
 * Designed for iOS WebView usage at /floor-embed
 *
 * Scaling strategy: cover the entire viewport (no black bars).
 * On portrait phones this scales to fill height and clips the sides slightly.
 * On landscape it scales to fill width and clips top/bottom slightly.
 */
const TradingFloorEmbed: React.FC = () => {
  const { snapshot, cycleSummary } = useMonitorData();
  const { statsMap, daySummary } = useModelStats();
  const { toggle: toggleMusic } = useFloorMusic();
  const [dialogData, setDialogData] = useState<TradeDialogData | null>(null);
  const [musicOn, setMusicOn] = useState(false);
  const [scale, setScale] = useState(1);

  const handleAvatarClick = useCallback((data: TradeDialogData) => {
    setDialogData({
      ...data,
      modelStats: statsMap[data.modelName] || null,
      daySummary: data.isManager ? daySummary : null,
    });
  }, [statsMap, daySummary]);

  const handleCloseDialog = useCallback(() => {
    setDialogData(null);
  }, []);

  const handleMusicToggle = useCallback(() => {
    const nowPlaying = toggleMusic();
    setMusicOn(nowPlaying);
  }, [toggleMusic]);

  // Cover the entire viewport — no black bars
  useEffect(() => {
    const updateScale = () => {
      // Use max to COVER (not fit) — fills the screen, clips overflow
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      // Fit entire scene on screen — no clipping
      setScale(Math.min(screenW / CANVAS_WIDTH, screenH / CANVAS_HEIGHT));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', () => setTimeout(updateScale, 150));
    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  const scaledW = CANVAS_WIDTH * scale;
  const scaledH = CANVAS_HEIGHT * scale;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#1a1d2e',
        overflow: 'hidden',
        /* Disable iOS bounce/overscroll and safe-area chrome */
        touchAction: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Center the scaled canvas so overflow clips evenly on both sides */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: scaledW,
          height: scaledH,
        }}
      >
        <div
          style={{
            width: 0,
            height: 0,
            overflow: 'visible',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <div
            className="floor-canvas-container"
            style={{
              width: CANVAS_WIDTH,
              height: CANVAS_HEIGHT,
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <PixiOffice
              snapshot={snapshot}
              cycleSummary={cycleSummary}
              onAvatarClick={handleAvatarClick}
            />
            {dialogData && (
              <TradeDialog data={dialogData} onClose={handleCloseDialog} />
            )}
          </div>
        </div>

        <button
          className="floor-music-btn"
          onClick={handleMusicToggle}
          title={musicOn ? 'Mute music' : 'Play music'}
        >
          {musicOn ? '\u266B' : '\u266A'}
        </button>
      </div>
    </div>
  );
};

export default TradingFloorEmbed;
